import {
  FulfillmentStatus,
  OrderLineItemDto,
  OrderSummaryDto,
  PaginatedResultDto_1OfOrderSummaryDto,
} from "@lib/ShiOneClient";
import {
  OrderLineItem,
  OrderRow,
} from "@features/orders-features/types/types";
import { SearchOrdersResult } from "@features/orders-features/hooks/useSearchOrders";
import {
  getInvoiceStatus,
  getShipTo,
  hasInvoice,
} from "@features/orders-features/functions/mapToOrderRowHelpers";
import { resolveItProjectOrderStatusValue } from "shared-ui/src/features/it-projects-feature";

const unknownOrderId = (order: OrderSummaryDto, index: number): string => {
  const a: string[] = ["unknown"];
  if (order.axNumber) {
    a.push(`ax-${order.axNumber}`);
  } else if (order.crmAccountId) {
    a.push(`crm-${order.crmAccountId}`);
  }
  if (order.orgUnitId) {
    a.push(`ou-${order.orgUnitId}`);
  }
  if (order.purchaseOrder) {
    a.push(`po-${order.purchaseOrder}`);
  }
  if (order.orderDate) {
    a.push(order.orderDate.toISOString());
  }
  a.push(index.toString());
  return a.join("|");
};

export function toOrderLines(order: OrderSummaryDto) {
  const groups = (order.fulfillmentGroups ?? {}) as Record<
    string,
    OrderLineItemDto[]
  >;

  const orderLineProducts = Object.entries(groups)
    .filter(([key]) => !key.startsWith(FulfillmentStatus.Shipped))
    .reduce<Record<string, OrderLineItemDto[]>>((acc, [key, lines]) => {
      const parts = key
        .replace(/[()]/g, "")
        .split(",")
        .map((s) => s.trim());
      const productType = parts[1];
      const electronicProductLabels: Record<string, string> = {
        SOFTWARE: "Email Delivery",
        SERVICE: "Services",
        FEES: "Fees",
      };
      const isInvoiced = order?.status?.toLowerCase() === "invoiced";
      const cleaned =
        isInvoiced && productType in electronicProductLabels
          ? electronicProductLabels[productType]
          : parts[0] === "NotApplicable"
            ? "Processing"
            : parts[0];
      acc[cleaned] = [...(acc[cleaned] ?? []), ...lines];
      return acc;
    }, {});

  const allLineItems: OrderLineItem[] = Object.entries(groups).flatMap(
    ([group, lines]) =>
      lines.map((lineItem) => ({
        ...lineItem,
        group,
      })),
  );

  return { orderLineProducts, allLineItems };
}

function toOrderRow(order: OrderSummaryDto, index: number): OrderRow {
  const { orderLineProducts, allLineItems } = toOrderLines(order);

  return {
    id:
      order.id ??
      (order.biOrderId != null ? `bi-${order.biOrderId}` : null) ??
      (order.webOrderId != null ? `web-${order.webOrderId}` : null) ??
      (order.salesOrderNumber ? `so-${order.salesOrderNumber}` : null) ??
      (order.customerPoNumber ? `cpo-${order.customerPoNumber}` : null) ??
      unknownOrderId(order, index),
    orgUnitId: order.orgUnitId,
    crmAccountId: order.crmAccountId ?? undefined,
    salesOrder: order.salesOrderNumber ?? "", // SalesOrderNumber = OrderNumber
    orderDate: order.orderDate ? order.orderDate.toISOString() : "",
    status:
      resolveItProjectOrderStatusValue({
        fulfillmentStatus: order.fulfillmentStatus,
        status: order.status,
        fulfillmentGroups: order.fulfillmentGroups,
      }) ?? "",
    purchaseOrder: order.customerPoNumber ?? order.purchaseOrder ?? "",
    storefront: order.orgUnitName ?? "",
    orderedBy: order.orderedBy ?? "",
    shipTo: getShipTo(order),
    totalItems: order.totalItems ?? 0,
    total: order.total ?? 0,
    currencyLocale: order.currencyLocale ?? null,
    currencyCode: order.currencyAbbr ?? null,
    invoiceStatus: getInvoiceStatus(order),
    webOrderId: order.webOrderId ?? null,
    biOrderId: order.biOrderId ?? null,
    hasInvoice: hasInvoice(order),
    orderLineProducts: orderLineProducts,
    allLineItems: allLineItems,
    canApprove: order.canApprove ?? false,
    daysPending: order.daysPending ?? 0,
  };
}

export default function mapToOrderRows(
  response: PaginatedResultDto_1OfOrderSummaryDto,
  fallback: { page: number; pageSize: number },
): SearchOrdersResult {
  return {
    items: (response.items ?? []).map(toOrderRow),
    pagination: {
      totalCount: response.totalCount ?? 0,
      pageNumber: response.pageNumber ?? fallback.page,
      pageSize: response.pageSize ?? fallback.pageSize,
      totalPages: response.totalPages ?? 0,
    },
  };
}
